import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ColDef, SelectionChangedEvent } from "ag-grid-community";
import { request } from "../../../common/api";
import {
  GET_BACKUP_DATAS,
  GET_DB_STORAGE_DATA,
} from "../../../common/query-keys";
import { BackupData } from "../../../common/types/backupData";
import { Buttons } from "../../../components/atoms/Buttons";
import { InputSelect } from "../../../components/atoms/Inputs/InputSelect";
import { Grid } from "../../../components/molecules/Grid";
import { PageContent } from "../../../components/organisms/Contents/PageContent";
import { DBDriveSection } from "../../../components/organisms/DBDriveSection";
import dayjs from "dayjs";
import { ChangeEvent, FormEvent, useState } from "react";
import { ReactComponent as IconDelete } from "../../../styles/assets/svg/icon_edit_delete.svg";
import { confirmDialog } from "../../../components/containers/ConfirmDialog";
import { BaseResponse } from "../../../common/types/baseRespones";
import {
  BackupStorageData,
  DbStorageData,
} from "../../../common/types/storageData";
import { convertToGB } from "../../../common/util/convertToGb";
import { fileDownload } from "../../../common/functions/download";
import { ModalLoading } from "../../../components/molecules/Modals/ModalLoading";
import { InputDateHour } from "../../../components/atoms/Inputs/InputDateHour";
import { SearchBar } from "../../../components/molecules/SearchBar";
import { validateDateRange } from "../../../common/functions/validateFunctions";
import { useRecoilValue } from "recoil";
import { currentShipState } from "../../../common/states/ship";

export interface BackupDataSearchInputs {
  shiptypeNo: string;
  shipNo: string;
  bupBackupType?: string;
  startDate?: string;
  endDate?: string;
}

export const DrivePage = () => {
  const queryClient = useQueryClient();
  const shipState = useRecoilValue(currentShipState);

  // search 조건 기본 값
  const [searchDefaultValues, setSearchDefaultValues] =
    useState<BackupDataSearchInputs>({
      shiptypeNo: shipState.shipTypeNo,
      shipNo: shipState.shipNo,
      bupBackupType: "",
      startDate: dayjs().subtract(7, "days").format("YYYY-MM-DD HH:mm:ss"),
      endDate: dayjs().format("YYYY-MM-DD HH:mm:ss"),
    });

  // search컨테이너에 들어갈 search 상태 변수
  const [searchInputs, setSearchInputs] =
    useState<BackupDataSearchInputs>(searchDefaultValues);

  const [selectedIds, setSelectedIds] = useState<(number | undefined)[]>([]);
  const [selectedFileName, setSelectedFileName] = useState<string | undefined>(
    ""
  );
  const [downloadLoading, setDownloadLoading] = useState(false);

  /** 백업파일 그리드 row 데이터  */
  const { data: backupDataList } = useQuery({
    queryKey: [GET_BACKUP_DATAS, searchInputs],
    queryFn: async () => {
      return (await request)<BaseResponse<BackupData[]>>({
        method: "GET",
        url: `/storage-management/backup-file-list`,
        params: searchInputs,
      }).then((res) => res.data);
    },
  });

  /** (SSD) DB 저장소 공간 상태
   *  (HDD) 백업 저장소 공간 상태
   * */
  const { data: storageData, isLoading } = useQuery({
    queryKey: [GET_DB_STORAGE_DATA],
    queryFn: async () => {
      return await request<
        BaseResponse<{
          backupStorageData: BackupStorageData;
          dbStorageData: DbStorageData;
        }>
      >({
        method: "GET",
        url: `/storage-management/db-storage-space`,
      }).then((res) => res.data);
    },
  });

  // 데이터 삭제
  const { mutateAsync: deleteBackupData } = useMutation({
    mutationFn: (bupId: number) => {
      return request<BaseResponse>({
        method: "DELETE",
        url: `/storage-management/${bupId}`,
      });
    },
    onSuccess: (res: BaseResponse) => {
      queryClient.invalidateQueries({ queryKey: [GET_BACKUP_DATAS] });
    },
  });

  /** 그리드 삭제 버튼 */
  const deleteButtonRender = (e: any) => {
    return (
      <Buttons
        type="button"
        size="sm"
        layout="icon"
        icon={<IconDelete />}
        onClick={async () => {
          const isConfirm = await confirmDialog({
            text: "선택한 항목을 삭제하시겠습니까?",
          });

          if (isConfirm) {
            deleteBackupData(e.data.bupId);
          }
        }}
      />
    );
  };

  const handleDownload = async () => {
    setDownloadLoading(true);
    try {
      const response = await request<any>({
        url: `/storage-management/download`,
        method: "POST",
        data: selectedIds,
        responseType: "blob",
      });

      // Content-Disposition 헤더에서 파일명 추출
      // const contentDisposition = response.headers["content-disposition"];
      // console.log("contentDisposition", contentDisposition);

      let filename = selectedFileName ?? `backup_data.csv`; // 기본 파일명

      // if (contentDisposition) {
      //   const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
      //   if (fileNameMatch?.[1]) {
      //     filename = fileNameMatch[1]; // 서버에서 받은 파일명으로 설정
      //   }
      // }

      fileDownload(response, filename);
    } catch (e) {
      console.log(e);
    } finally {
      setDownloadLoading(false);
    }
  };

  const onSelectionChanged = (e: SelectionChangedEvent<BackupData>) => {
    try {
      const selectedNodes = e.api?.getSelectedNodes();

      const selectedIds = selectedNodes?.map((node) => node.data?.bupId);

      setSelectedIds(selectedIds ?? []);
      setSelectedFileName(selectedNodes[0]?.data?.bupFileName || "");
    } catch (e) {
      console.error(e);
    }
  };

  /** 조회조건에 따라 검색시 실행되는 함수 */
  const handleSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const isValid = validateDateRange(
      searchDefaultValues.startDate,
      searchDefaultValues.endDate,
      30
    );

    if (!isValid) {
      return;
    }

    setSearchInputs(searchDefaultValues);
  };

  const columnDefs: ColDef[] = [
    {
      headerName: "선택",
      checkboxSelection: true,
      width: 50,
    },
    {
      headerName: "No",
      valueGetter: "node.rowIndex + 1",
      width: 80,
    },
    {
      headerName: "백업유형",
      field: "bupBackupType",

      valueFormatter: (params) => {
        const backupType = params.value;
        return backupType === "manual" ? "수동" : "자동";
      },
      flex: 1,
    },
    {
      headerName: "파일 생성 시간",
      field: "bupTime",
      valueFormatter: (params) => {
        return dayjs(params.value).format("YYYY-MM-DD HH:mm:ss");
      },
      flex: 1,
    },

    {
      headerName: "파일명",
      field: "bupFileName",
      flex: 1,
    },
    {
      headerName: "용량",
      field: "bupStorage",
      valueFormatter: (params) => {
        return convertToGB(params.value) + "GB";
      },
      flex: 1,
    },
    {
      headerName: "삭제",
      cellRenderer: deleteButtonRender,
      width: 50,
    },
  ];

  return (
    <PageContent
      depth01Title="데이터 통합관리"
      depth02Title="DB 관리"
      depth03Title="저장소 관리"
    >
      <>
        <DBDriveSection
          drive={
            storageData?.backupStorageData && storageData?.dbStorageData
              ? {
                  ssd: {
                    // 총 저장소 용량
                    total:
                      storageData?.dbStorageData?.dssTotalStorageCapacity || 0,
                    // 시스템 사용량
                    system: storageData?.dbStorageData?.dssSystemUsage || 0,
                    //DB 저장소 사용량
                    used: storageData?.dbStorageData?.dssTotalSpaceUsed || 0,
                    // 잔여공간
                    avail: storageData?.dbStorageData?.dssRemainSpace || 0,
                  },
                  hdd: {
                    // 총 저장소 용량
                    total:
                      storageData?.backupStorageData?.bssTotalStorageCapacity ||
                      0,
                    // 전체 사용 공간
                    used:
                      storageData?.backupStorageData?.bssTotalSpaceUsed || 0,
                    // 시스템 사용량
                    system: storageData?.backupStorageData?.bssSystemUsage || 0,
                    // 잔여공간
                    avail: storageData?.backupStorageData?.bssRemainSpace || 0,
                    // 수동 백업 용량
                    manualBackup:
                      storageData?.backupStorageData?.bssManualBackupCapacity ||
                      0,
                    // 자동 백업 용량
                    autoBackup:
                      storageData?.backupStorageData?.bssAutoBackupCapacity ||
                      0,
                  },
                }
              : {
                  hdd: {
                    // 총 저장소 용량
                    total: 0,
                    // 전체 사용 공간
                    used: 0,
                    // 시스템 사용량
                    system: 0,
                    // 잔여공간
                    avail: 0,
                    // 수동 백업 용량
                    manualBackup: 0,
                    // 자동 백업 용량
                    autoBackup: 0,
                  },
                  ssd: {
                    // 총 저장소 용량
                    total: 0,
                    // 시스템 사용량
                    system: 0,
                    //DB 저장소 사용량
                    used: 0,
                    // 잔여공간
                    avail: 0,
                  },
                }
          }
          isLoading={isLoading}
        />
        <SearchBar
          onSearch={handleSearch}
          searchInput={
            <>
              <InputSelect
                size="md"
                name="bupBackupType"
                placeholder="백업유형 선택"
                width={120}
                options={[
                  { value: "manual", label: "수동" },
                  { value: "auto", label: "자동" },
                ]}
              />
              <InputDateHour
                name="startDate"
                size="md"
                width={200}
                value={searchDefaultValues?.startDate}
                defaultValue={searchDefaultValues?.startDate}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setSearchDefaultValues({
                    ...searchDefaultValues,
                    startDate: dayjs(e.target.value).format(
                      "YYYY-MM-DD HH:mm:ss"
                    ),
                    endDate: dayjs(e.target.value)
                      .add(1, "month")
                      .format("YYYY-MM-DD HH:mm:ss"),
                  });
                }}
              />
              ~
              <InputDateHour
                name="endDate"
                size="md"
                width={200}
                value={searchDefaultValues?.endDate}
                defaultValue={searchDefaultValues?.endDate}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  setSearchDefaultValues({
                    ...searchDefaultValues,
                    endDate: e.target.value,
                  });
                }}
              />
              <Buttons
                type="submit"
                size="md"
                layout="selectCondition"
                label="전월"
                onClick={() => {
                  setSearchDefaultValues({
                    ...searchDefaultValues,
                    startDate: dayjs()
                      .subtract(1, "month")
                      .format("YYYY-MM-DD HH:mm:ss"),
                    endDate: dayjs().format("YYYY-MM-DD HH:mm:ss"),
                  });
                }}
              />
            </>
          }
          gridButtons={
            <Buttons
              type="button"
              size="md"
              layout="primary"
              label="선택 다운로드"
              onClick={handleDownload}
              disabled={selectedIds.length === 0 || downloadLoading}
            />
          }
        />

        <Grid
          rowData={backupDataList}
          columnDefs={columnDefs}
          rowSelection={"single"}
          onSelectionChanged={onSelectionChanged}
          paging={false}
          pagingShow={false}
        />
        {downloadLoading && <ModalLoading modalText="다운로드 중입니다..." />}
      </>
    </PageContent>
  );
};
